import {
  isBuyDirectLocation,
  getLocationDeliveryDetail,
  getAvailableOrderTypeCodes,
  getAvailableTransports,
  hashTransport,
  cartSlice,
  defaultDeliveryDetails,
  orderTypeOptions,
} from 'cart'
import { ProductLocationModel } from 'interfaces'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { DeliveryDetail, Transport } from '../../../../src/api/cart/interfaces'
import { StoreInstances } from '../../../../src/store/StoreInstancesContainer'
import { breakpoints } from '../../../../src/theme/breakpoints'
import SelectCustomField from './SelectField'
import TransportsDropdown from './TransportDropdown'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, store } from 'store'
import { Vehicle } from 'src/store/models/Vehicles'
import { isEqual } from 'lodash-es'
import { MY_PLACE_SELLER } from 'common'

interface IOrderTypeTransportRow {
  locations: ProductLocationModel[]
  vehicle: Vehicle
}

const resolveTransport = (
  availableTransports: Transport[],
  persisted?: DeliveryDetail
): string | undefined => {
  if (persisted && availableTransports.length > 0) {
    const { carrier, shipCode, releaseCode } = persisted
    const match = availableTransports.find(
      (t) =>
        t.carrier === carrier &&
        t.shipCode === shipCode &&
        t.releaseCode === releaseCode
    )

    return match ? hashTransport(match) : availableTransports[0]?.id
  }

  return availableTransports[0]?.id
}

export const OrderTypeTransportRow = observer(
  (props: IOrderTypeTransportRow): ReactElement => {
    const { locations, vehicle } = props
    const isBuyDirect = isBuyDirectLocation(locations[0])

    // Convert locations to string so useEffect runs when location changes
    // (locations array gets new reference every render, breaking useEffect)
    const locationString = locations
      .map((loc) => loc.locationId)
      .sort()
      .join()

    const dispatch = useDispatch()

    /*
    If the cart has no products from buy direct or virtual buy direct locations, then this component does not receive location 
    prop. Then the below variable `nonBuyDirectCart` is truthy.
    */
    const actualLocationId = locations[0].locationId.toString()
    // Use the actual location's transports if available, otherwise fall back to
    // primary location '100'. This handles alternate locations (e.g. 102) that
    // have their own transport data, while keeping the fallback for locations
    // that don't have dedicated transport entries.
    const locationId = useSelector((state: RootState) => {
      const hasAvailableOrderTypes =
        getAvailableOrderTypeCodes(state, actualLocationId).length > 0
      const useActualLocation = hasAvailableOrderTypes || isBuyDirect
      return useActualLocation ? actualLocationId : '100'
    })

    const availableOrderTypes = useSelector((state: RootState) =>
      getAvailableOrderTypeCodes(state, locationId)
    )

    const orderTypes = orderTypeOptions.filter((option) =>
      availableOrderTypes.includes(option.value)
    )
    /*When there is no Regular order type for a location then we add and show Regular order type as a first option in the dropdown.*/
    const isRegularTransportPresent = orderTypes.find(
      (type) => type.value === 'R'
    )
    if (!isRegularTransportPresent) {
      orderTypes.unshift(orderTypeOptions[0])
    }

    const configuredOrderType = orderTypes.find(
      (type) =>
        type.label === StoreInstances.userStore.preferences.cart_orderType
    )?.value

    // True when the configured order type has at least one transport at this
    // location.
    const configuredOrderTypeHasTransports = useSelector((state: RootState) =>
      configuredOrderType
        ? getAvailableTransports(state, locationId, configuredOrderType)
            .length > 0
        : false
    )

    // Use the configured order type when it has transports; otherwise default
    // to Regular. Empty while the order types are still loading.
    const resolveDefaultOrderType = (): string => {
      if (!orderTypes?.length) return ''
      if (configuredOrderType && configuredOrderTypeHasTransports) {
        return configuredOrderType
      }
      return 'R'
    }
    const defaultOrderType = resolveDefaultOrderType()

    // Read the delivery detail once at mount time via a ref to avoid
    // re-triggering the transport effect after each dispatch overwrites it.
    const initialDeliveryDetailRef = useRef(
      getLocationDeliveryDetail(
        store.getState(),
        vehicle,
        MY_PLACE_SELLER.coverageKey, // Only MyPlace supports delivery details for now
        actualLocationId
      )
    )

    // When the actual cart location changes, refresh the ref synchronously
    // so persistedOrderType reads the correct delivery detail for the new location.
    const prevActualLocationIdRef = useRef(actualLocationId)
    if (prevActualLocationIdRef.current !== actualLocationId) {
      prevActualLocationIdRef.current = actualLocationId
      initialDeliveryDetailRef.current = getLocationDeliveryDetail(
        store.getState(),
        vehicle,
        MY_PLACE_SELLER.coverageKey,
        actualLocationId
      )
    }

    const persistedOrderType = useMemo(() => {
      const deliveryDetail = initialDeliveryDetailRef.current
      if (!deliveryDetail?.deliveryMethod) return ''

      // Cart creation seeds every location with `defaultDeliveryDetails`,
      // whose deliveryMethod is 'Regular' but whose transport fields are
      // empty. Treat the persisted detail as an actual selection only when it
      // carries transport data, so this seeded placeholder does not override
      // the computed default.
      const hasExplicitSelection = Boolean(
        deliveryDetail.carrier ||
          deliveryDetail.shipCode ||
          deliveryDetail.releaseCode
      )
      if (!hasExplicitSelection) return ''

      return (
        orderTypes.find((t) => t.label === deliveryDetail.deliveryMethod)
          ?.value ?? ''
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualLocationId, orderTypes])

    const [transports, setTransports] = useState([])
    const [orderType, setOrderType] = useState('')
    const [transport, setTransport] = useState('')

    // Only show the transport dropdown when the user actually has a choice to
    // make. When a single transport code is available for the selected order
    // type, there is nothing to pick, so the dropdown is hidden.
    const showTransportDropdown = transports?.length > 1

    const availableTransports = useSelector(
      (state: RootState) =>
        getAvailableTransports(state, locationId, orderType),
      (left, right) => isEqual(left, right)
    )

    useEffect(() => {
      if (!orderType) return

      setTransports(availableTransports)
      setTransport(
        resolveTransport(availableTransports, initialDeliveryDetailRef.current)
      )
    }, [orderType, locationId, availableTransports])

    useEffect(() => {
      if (!orderType) return

      const selectedOrderTransport: Transport = transports?.find(
        (at) => hashTransport(at) === transport
      )

      const orderTransport = selectedOrderTransport || defaultDeliveryDetails

      if (!orderTransport) return

      locations.forEach((loc) => {
        const deliveryMethod = orderTypeOptions.find(
          (option) => option.value === orderType
        )?.label

        const deliveryDetail: DeliveryDetail = {
          deliveryMethod,
          name: '',
          attentionToUser: '',
          carrier: orderTransport?.carrier || '',
          shipCode: orderTransport?.shipCode || '',
          releaseCode: orderTransport?.releaseCode || '',
        }

        dispatch(
          cartSlice.actions.updateLocationDeliveryDetails({
            vehicle,
            locationId: loc.locationId,
            deliveryDetail,
          })
        )
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transport, orderType, locationString])

    useEffect(() => {
      setOrderType(persistedOrderType || defaultOrderType)
    }, [defaultOrderType, persistedOrderType])

    return (
      <DropdownTableData>
        <SelectCustomField
          value={orderType}
          options={orderTypes}
          onChange={(value) => setOrderType(value as string)}
          title="orderType"
          disabled={orderTypes.length === 1}
          cursorType="pointer"
        />
        {showTransportDropdown && (
          <TransportsDropdown
            value={transport}
            transports={transports}
            onChange={setTransport}
          />
        )}
      </DropdownTableData>
    )
  }
)

const DropdownTableData = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`
